import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  IconButton,
  Stack,
  Alert,
  CircularProgress,
  Paper,
  FormControlLabel,
  Switch,
  Tooltip
} from '@mui/material';
import {
  DatePicker
} from '@mui/x-date-pickers';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format, isValid } from 'date-fns';
import { es } from 'date-fns/locale';

const MultipleDatePicker = ({ 
  onDatesChange, 
  selectedDates = [], 
  especialistaId, 
  horaInicio, 
  horaFin,
  disabled = false 
}) => {
  const [open, setOpen] = useState(false);
  const [tempDates, setTempDates] = useState([]);
  const [newDate, setNewDate] = useState(null);
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [autoCheck, setAutoCheck] = useState(true);

  // Inicializar fechas temporales cuando se abre el diálogo
  useEffect(() => {
    if (open) {
      setTempDates([...selectedDates]);
    }
  }, [open, selectedDates]);

  // Verificar disponibilidad automáticamente cuando cambian las fechas
  useEffect(() => {
    if (autoCheck && tempDates.length > 0 && especialistaId && horaInicio && horaFin) {
      checkAvailability();
    }
  }, [tempDates, especialistaId, horaInicio, horaFin, autoCheck]);

  // Verificar disponibilidad de fechas (simulado por ahora)
  const checkAvailability = async () => {
    if (!especialistaId || !horaInicio || !horaFin || tempDates.length === 0) {
      return;
    }

    setChecking(true);
    try {
      // Simular llamada al servicio
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simular respuesta (70% disponibles)
      const mockAvailability = tempDates.map(date => ({
        fecha: format(date, 'yyyy-MM-dd'),
        disponible: Math.random() > 0.3,
        conflicto: Math.random() > 0.3 ? null : {
          hora_inicio: '10:00:00',
          hora_fin: '11:00:00',
          paciente: 123
        }
      }));

      setAvailability(mockAvailability);
    } catch (error) {
      console.error('Error verificando disponibilidad:', error);
      setAvailability([]);
    } finally {
      setChecking(false);
    }
  };

  // Agregar nueva fecha
  const handleAddDate = () => {
    if (!newDate || !isValid(newDate)) return;

    // Verificar que no esté duplicada
    const isDuplicate = tempDates.some(date => 
      format(date, 'yyyy-MM-dd') === format(newDate, 'yyyy-MM-dd')
    );

    if (isDuplicate) {
      return;
    }

    // Verificar que no sea una fecha pasada
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (newDate < today) {
      return;
    }

    const updatedDates = [...tempDates, newDate].sort((a, b) => a - b);
    setTempDates(updatedDates);
    setNewDate(null);
  };

  // Eliminar fecha
  const handleRemoveDate = (dateToRemove) => {
    const updatedDates = tempDates.filter(date => 
      format(date, 'yyyy-MM-dd') !== format(dateToRemove, 'yyyy-MM-dd')
    );
    setTempDates(updatedDates);
  };

  // Confirmar selección
  const handleConfirm = () => {
    onDatesChange(tempDates);
    setOpen(false);
  };

  // Cancelar
  const handleCancel = () => {
    setTempDates([...selectedDates]);
    setOpen(false);
  };

  // Obtener estado de disponibilidad para una fecha
  const getDateAvailability = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availability.find(a => a.fecha === dateStr);
  };

  // Obtener color del chip según disponibilidad
  const getChipColor = (date) => {
    if (checking) return 'default';
    
    const avail = getDateAvailability(date);
    if (!avail) return 'default';
    
    return avail.disponible ? 'success' : 'error';
  };

  // Obtener ícono del chip según disponibilidad
  const getChipIcon = (date) => {
    if (checking) return <CircularProgress size={16} />;
    
    const avail = getDateAvailability(date);
    if (!avail) return <EventIcon />;
    
    return avail.disponible ? <CheckCircleIcon /> : <ErrorIcon />;
  };

  return (
    <Box>
      {/* Botón para abrir selector */}
      <Button
        variant="outlined"
        startIcon={<EventIcon />}
        onClick={() => setOpen(true)}
        disabled={disabled}
        fullWidth
        sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
      >
        {selectedDates.length === 0 
          ? 'Seleccionar múltiples fechas'
          : `${selectedDates.length} fecha${selectedDates.length > 1 ? 's' : ''} seleccionada${selectedDates.length > 1 ? 's' : ''}`
        }
      </Button>

      {/* Mostrar fechas seleccionadas como chips */}
      {selectedDates.length > 0 && (
        <Box mt={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {selectedDates.map((date, index) => (
              <Chip
                key={index}
                label={format(date, 'dd/MM/yyyy')}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Diálogo de selección */}
      <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <EventIcon color="primary" />
              <Typography variant="h6">
                Seleccionar Múltiples Fechas
              </Typography>
            </Stack>
            <IconButton onClick={handleCancel} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          <Box mb={3}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Selecciona todas las fechas en las que quieres programar la cita con las mismas condiciones 
              (paciente, especialista, especialidad y horario).
            </Typography>
          </Box>

          {/* Configuración de verificación automática */}
          <Box mb={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoCheck}
                  onChange={(e) => setAutoCheck(e.target.checked)}
                  color="primary"
                />
              }
              label="Verificar disponibilidad automáticamente"
            />
          </Box>

          {/* Agregar nueva fecha */}
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Agregar Fecha
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <DatePicker
                  label="Seleccionar fecha"
                  value={newDate}
                  onChange={setNewDate}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddDate}
                  disabled={!newDate || !isValid(newDate)}
                  fullWidth
                >
                  Agregar
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Lista de fechas seleccionadas */}
          {tempDates.length > 0 && (
            <Paper elevation={1} sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2">
                  Fechas Seleccionadas ({tempDates.length})
                </Typography>
                {autoCheck && !checking && availability.length > 0 && (
                  <Button
                    size="small"
                    onClick={checkAvailability}
                    startIcon={<CheckCircleIcon />}
                  >
                    Verificar Disponibilidad
                  </Button>
                )}
              </Box>

              <Grid container spacing={1}>
                {tempDates.map((date, index) => {
                  const avail = getDateAvailability(date);
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box display="flex" alignItems="center" spacing={1}>
                        <Tooltip 
                          title={
                            checking ? 'Verificando...' :
                            avail ? (avail.disponible ? 'Disponible' : `Conflicto: ${avail.conflicto?.hora_inicio} - ${avail.conflicto?.hora_fin}`) :
                            'No verificado'
                          }
                        >
                          <Chip
                            icon={getChipIcon(date)}
                            label={format(date, 'EEE dd/MM/yyyy', { locale: es })}
                            color={getChipColor(date)}
                            variant={avail?.disponible === false ? 'filled' : 'outlined'}
                            sx={{ flex: 1, justifyContent: 'flex-start' }}
                          />
                        </Tooltip>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveDate(date)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Resumen de disponibilidad */}
              {availability.length > 0 && !checking && (
                <Box mt={2}>
                  <Alert 
                    severity={availability.every(a => a.disponible) ? 'success' : 'warning'}
                    icon={availability.every(a => a.disponible) ? <CheckCircleIcon /> : <WarningIcon />}
                  >
                    <Typography variant="body2">
                      <strong>Disponibilidad:</strong> {availability.filter(a => a.disponible).length} de {availability.length} fechas disponibles
                      {availability.some(a => !a.disponible) && (
                        <span> - Hay conflictos en algunas fechas</span>
                      )}
                    </Typography>
                  </Alert>
                </Box>
              )}

              {checking && (
                <Box mt={2} display="flex" alignItems="center" justifyContent="center">
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Verificando disponibilidad...
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirm} 
            variant="contained"
            disabled={tempDates.length === 0}
          >
            Confirmar ({tempDates.length} fecha{tempDates.length > 1 ? 's' : ''})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MultipleDatePicker;